import { css, html, LitElement, TemplateResult } from "lit";
import { customElement, state } from "lit/decorators.js";
import { READING_BANDS } from "../shared/bands.js";
import { ReadingBand } from "../shared/type.app.js";
import { authSession, getAuthUser, isAuthConfigured, loginInstructor, type AuthUser } from "./auth0.js";
import { WarningEvent } from "./event.warning.js";
import { appStore } from "./store.js";
import { globalStyles } from "./styles.global.js";
import { dispatch } from "./util.events.js";

@customElement("reading-bee-onboarding")
export class ReadingBeeOnboarding extends LitElement {
  static override styles = [
    globalStyles,
    css`
      :host {
        display: block;
        min-height: 100%;
      }

      .screen {
        min-height: 100dvh;
        display: grid;
        place-items: center;
        padding: calc(1.5rem + var(--inset-top)) 1.25rem calc(1.5rem + env(safe-area-inset-bottom));
      }

      .card {
        width: min(560px, 100%);
        display: grid;
        gap: 1.2rem;
      }

      .brand {
        display: grid;
        justify-items: center;
        text-align: center;
        gap: 0.6rem;
      }

      img {
        width: 88px;
        height: 88px;
        border-radius: 22px;
        box-shadow: var(--shadow-normal);
      }

      .lede {
        font-size: 1.02rem;
        max-width: 28ch;
      }

      .signed-in {
        margin: 0;
        font-size: 0.92rem;
      }

      .signed-in b {
        color: var(--color-primary-text);
      }

      .brand .sign-in {
        width: auto;
        min-width: 10rem;
      }

      label {
        display: grid;
        gap: 0.45rem;
        font-size: 0.92rem;
        color: var(--color-primary-text-muted);
      }

      .bands {
        display: grid;
        gap: 0.7rem;
      }

      .band {
        width: 100%;
        box-sizing: border-box;
        text-align: left;
        padding: 1rem 1.1rem;
        border-radius: 18px;
        background: #1a1713;
        border: 1px solid var(--color-panel-border);
        display: grid;
        gap: 0.15rem;
        transition:
          border-color var(--time-normal) ease,
          box-shadow var(--time-normal) ease,
          background var(--time-normal) ease;
      }

      .band:hover {
        border-color: var(--color-1);
        background: #221e18;
      }

      .band[active] {
        border-color: var(--color-1);
        box-shadow: 0 0 0 3px rgba(232, 184, 74, 0.16);
      }

      .band[active]:hover {
        background: #221e18;
        box-shadow: 0 0 0 3px rgba(232, 184, 74, 0.28);
      }

      .band strong {
        font-size: 1.05rem;
      }

      .band span {
        color: var(--color-primary-text-muted);
        font-size: 0.92rem;
      }

      .primary-btn {
        width: 100%;
        margin-top: 0.3rem;
      }

      .primary-btn:disabled {
        opacity: 0.4;
        cursor: not-allowed;
        transform: none;
      }
    `,
  ];

  @state() private name = "";
  @state() private band: ReadingBand | null = null;
  @state() private authUser: AuthUser | null = null;
  @state() private authBusy = false;

  override connectedCallback(): void {
    super.connectedCallback();
    this.authUser = getAuthUser();
    authSession.addEventListener("change", this.onAuthChange);
  }

  override disconnectedCallback(): void {
    super.disconnectedCallback();
    authSession.removeEventListener("change", this.onAuthChange);
  }

  private onAuthChange = (): void => {
    this.authUser = getAuthUser();
  };

  override render(): TemplateResult {
    const canStart = this.name.trim().length > 0;
    return html`
      <div class="screen">
        <div class="card">
          <div class="brand">
            <img src="/logo/logo-256x256.png" alt="Reading Bee" />
            <h1>Reading Bee</h1>
            ${this.signInControl()}
            <p class="lede">A quiet place to practice reading, math, and more, one line at a time.</p>
          </div>
          <label>
            Who is reading?
            <input type="text" maxlength="40" placeholder="Student name" .value=${this.name} @input=${this.onName} />
          </label>
          <div class="bands">
            ${READING_BANDS.map(
              (band) => html`
                <button
                  class="band"
                  ?active=${this.band === band.id}
                  @click=${() => (this.band = this.band === band.id ? null : band.id)}>
                  <strong>${band.label}</strong>
                  <span>${band.detail}</span>
                </button>
              `,
            )}
          </div>
          <button class="primary-btn" ?disabled=${!canStart} @click=${this.start}>Start reading</button>
        </div>
      </div>
    `;
  }

  private signInControl(): TemplateResult {
    if (this.authUser) {
      const label = this.authUser.name || this.authUser.email || "Instructor";
      return html`
        <p class="signed-in">
          Signed in as
          <b>${label}</b>
        </p>
      `;
    }
    return html`
      <button class="ghost-btn sign-in" ?disabled=${this.authBusy || !isAuthConfigured()} @click=${this.signIn}>
        Sign in
      </button>
    `;
  }

  private signIn = async (): Promise<void> => {
    this.authBusy = true;
    try {
      await loginInstructor();
    } catch {
      this.authBusy = false;
      dispatch(this, WarningEvent("Could not start sign-in"));
    }
  };

  private onName = (event: Event): void => {
    this.name = (event.target as HTMLInputElement).value;
  };

  private start = (): void => {
    if (!this.name.trim()) return;
    appStore.createFirstProfile(this.name, this.band ?? "letters");
  };
}
